import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import { invalidateJobNotificationQueries, useNurseJobNotificationsQuery, useSaveNurseJobNotifications } from "../../../../../hooks/useNurseJobNotifications";
import { computeJobNotificationChanges, type JobSilentOverrides } from "./jobNotificationDefaults";

/** What the edit-mode Job table reads and writes. The profile page owns it so Save can send it. */
export type JobNotificationDraft = {
	overrides: JobSilentOverrides;
	setOverride: (jobId: string, isSilent: boolean) => void;
	/** The job list when the edit started. A job not in it is new. Empty for a new profile. */
	originalJobIds: readonly string[];
};

type Params = {
	departmentId: string;
	/** `undefined` for a new profile, which has nothing saved yet. */
	nurseId: number | undefined;
	originalJobIds: readonly string[];
};

/**
 * Edit-mode notification switches, kept as a draft.
 * They are sent after the job list saves, because that save applies the "new job starts silent" rule first.
 */
export const useJobNotificationDraft = ({ departmentId, nurseId, originalJobIds }: Params) => {
	const [overrides, setOverrides] = useState<JobSilentOverrides>({});
	const { silentByJobId } = useNurseJobNotificationsQuery(departmentId, nurseId);
	const { mutateAsync } = useSaveNurseJobNotifications(departmentId);
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	const { showErrorSnackbar } = useHospitalData();
	const { t } = useTranslation();

	const setOverride = useCallback((jobId: string, isSilent: boolean) => setOverrides((prev) => ({ ...prev, [jobId]: isSilent })), []);
	const resetDraft = useCallback(() => setOverrides({}), []);

	/** Call after the job list saves. Never throws: the profile already saved, so errors are reported here. */
	const persistDraft = useCallback(
		async (savedNurseId: number, finalJobIds: readonly string[]): Promise<void> => {
			const changes = computeJobNotificationChanges({ originalJobIds, finalJobIds, loadedSilentByJobId: silentByJobId, overrides });
			resetDraft();
			if (changes.length === 0) {
				// Saving the job list may have added silent jobs.
				await invalidateJobNotificationQueries(queryClient, departmentId, savedNurseId);
				return;
			}
			const analytics = { nurse_id: savedNurseId, change_count: changes.length };
			track("profile_job_notifications_draft_save_start", analytics);
			try {
				await mutateAsync({ nurseId: savedNurseId, changes });
				track("profile_job_notifications_draft_save_success", analytics);
			} catch (error) {
				track("profile_job_notifications_draft_save_error", { ...analytics, error_message: error instanceof Error ? error.message : String(error) });
				showErrorSnackbar(t("jobNotifications.profile.draftSaveFailed"), analytics, error);
				await invalidateJobNotificationQueries(queryClient, departmentId, savedNurseId);
			}
		},
		[originalJobIds, silentByJobId, overrides, resetDraft, queryClient, departmentId, track, mutateAsync, showErrorSnackbar, t],
	);

	const draft = useMemo<JobNotificationDraft>(() => ({ overrides, setOverride, originalJobIds }), [overrides, setOverride, originalJobIds]);

	return { draft, persistDraft, resetDraft };
};
